import { DATA_HUB } from './data-hub.definition.js';
import { ADMIN_HUB } from './admin-hub.definition.js';
import { ORGANIZATION_ADMIN_HUB } from './organization-admin-hub.definition.js';
const DEFERRED_MODULES = new Set([
    'ACCOUNTING',
    'TAX',
    'REGULATORY',
    'FACTS',
    'MARGIN_TRADING',
    'ORDER_MANAGEMENT',
]);
/** Core operational cards that are source-complete for the released PMS Data tab. */
const RELEASED_DATA_CARD_IDS = new Set([
    'card.corporateActions',
]);
/**
 * Source-complete organization administration cards promoted into the released
 * single-org product. Roles and access inspector intentionally retain their
 * source WIP status for the first release: roles are predefined/read-only and
 * access inspection is not a production surface yet.
 */
const RELEASED_ORGANIZATION_CARD_IDS = new Set([
    'card.organization.clientAccounts',
    'card.organization.portfolios',
    'card.organization.taxRules',
    'card.organization.feeRules',
    'card.organization.audit',
]);
const DATA_ADMINISTRATION_CARD_IDS = new Set([
    'card.instruments',
    'card.clientsData',
    'card.portfoliosData',
    'card.settlementInstructions',
    'card.marketPrices',
]);
const DATA_REPORT_CARD_IDS = new Set(['card.performance']);
const DATA_DEFERRED_CARD_IDS = new Set([
    'card.mandates',
    'card.journals',
    'card.chartOfAccounts',
    'card.fiscalPeriods',
    'card.taxRecords',
    'card.complianceBreaches',
    'card.riskExposures',
    'card.evidence',
    'card.factBatches',
    'card.fxRates',
    'card.benchmarks',
    'card.marketDataProviders',
    'card.marketDataExceptions',
    'card.managementFees',
    'card.performanceFees',
]);
function isAvailable(card) {
    return (card.availability ?? card.implementationStatus ?? 'AVAILABLE') === 'AVAILABLE';
}
function isComposable(card) {
    return isAvailable(card);
}
function promoteReleasedOrganizationCard(card) {
    return RELEASED_ORGANIZATION_CARD_IDS.has(card.id)
        ? { ...card, wip: false, availability: 'AVAILABLE' }
        : card;
}
function promoteReleasedDataCard(card) {
    return RELEASED_DATA_CARD_IDS.has(card.id)
        ? { ...card, implementationStatus: 'AVAILABLE', wip: false }
        : card;
}
function activeCards(group) {
    return group.cards
        .filter((card) => (RELEASED_ORGANIZATION_CARD_IDS.has(card.id) ||
        !DEFERRED_MODULES.has(String(card.module))) &&
        !DATA_DEFERRED_CARD_IDS.has(card.id) &&
        isComposable(card))
        .map(promoteReleasedOrganizationCard);
}
function sourceDataCard(id) {
    return DATA_HUB.groups.flatMap((group) => group.cards).find((card) => card.id === id);
}
function sourceOrganizationCard(id) {
    return ORGANIZATION_ADMIN_HUB.groups.flatMap((group) => group.cards).find((card) => card.id === id);
}
export const PORTFOLIO_MANAGEMENT_DATA_HUB = Object.freeze({
    ...DATA_HUB,
    groups: DATA_HUB.groups
        .map((group) => ({
        ...group,
        cards: group.cards
            .filter((card) => !DATA_ADMINISTRATION_CARD_IDS.has(card.id) &&
            !DATA_REPORT_CARD_IDS.has(card.id) &&
            !DATA_DEFERRED_CARD_IDS.has(card.id) &&
            !DEFERRED_MODULES.has(String(card.module)) &&
            isComposable(card))
            .map(promoteReleasedDataCard),
    }))
        .filter((group) => group.cards.length > 0),
});
function organizationCard(source, id) {
    return source?.operationId && isComposable(source)
        ? {
            ...source,
            id,
            owner: 'investment',
            availability: 'AVAILABLE',
            wip: false,
            operationId: source.operationId,
        }
        : undefined;
}
function organizationLifecycleCard(id) {
    const source = sourceOrganizationCard(id);
    if (!source?.operationId || !isComposable(source))
        return undefined;
    const { condition: _runtimeCondition, ...base } = source;
    return {
        ...base,
        id,
        availability: 'AVAILABLE',
        wip: base.wip ?? false,
        operationId: source.operationId,
    };
}
const organizationInstrumentCard = organizationCard(sourceDataCard('card.instruments'), 'card.organization.instruments');
const organizationMarketDataCard = organizationCard(sourceDataCard('card.marketPrices'), 'card.organization.marketData');
const organizationSettlementInstructionsCard = organizationCard(sourceDataCard('card.settlementInstructions'), 'card.organization.settlementInstructions');
const organizationBranchCard = (() => {
    const source = sourceOrganizationCard('card.organization.branches');
    if (!source || !isComposable(source))
        return undefined;
    return {
        ...source,
        id: 'card.organization.branches',
        wip: false,
        availability: 'AVAILABLE',
    };
})();
const organizationOpeningCutoverCard = organizationLifecycleCard('card.organization.openingCutover');
const ORGANIZATION_CARD_GROUPS = {
    'organization-admin-organization': [
        'card.organization.profile',
        'card.organization.moduleAssignments',
    ],
    'organization-admin-people-access': [
        'card.organization.users',
        'card.organization.roles',
    ],
    'organization-admin-client-portfolio': [
        'card.organization.clients',
        'card.organization.clientAccounts',
        'card.organization.portfolios',
    ],
    'organization-admin-reference-data': [
        'card.organization.taxRules',
        'card.organization.feeRules',
    ],
    'organization-admin-assurance': [
        'card.organization.audit',
        'card.organization.accessInspector',
    ],
};
const GROUP_KEYS = {
    'organization-admin-organization': {
        title: 'hub.admin.organizationSetup.title',
        description: 'hub.admin.organizationSetup.description',
    },
    'organization-admin-people-access': {
        title: 'hub.admin.peopleAccess.title',
        description: 'hub.admin.peopleAccess.description',
    },
    'organization-admin-client-portfolio': {
        title: 'hub.admin.clientPortfolio.title',
        description: 'hub.admin.clientPortfolio.description',
    },
    'organization-admin-reference-data': {
        title: 'hub.admin.referenceData.title',
        description: 'hub.admin.referenceData.description',
    },
    'organization-admin-assurance': {
        title: 'hub.admin.assurance.title',
        description: 'hub.admin.assurance.description',
    },
};
function groupFrom(template, id, cards) {
    const keys = GROUP_KEYS[id];
    return {
        ...template,
        id,
        telemetryId: `hub.admin.${id}`,
        accessibilityLabelKey: keys.title,
        titleKey: keys.title,
        descriptionKey: keys.description,
        cards,
    };
}
function projectOrganizationGroups(groups) {
    const active = groups.flatMap(activeCards);
    const template = groups[0];
    if (!template)
        return [];
    return Object.entries(ORGANIZATION_CARD_GROUPS).flatMap(([id, cardIds]) => {
        const cards = active.filter((card) => cardIds.includes(card.id));
        if (id === 'organization-admin-organization') {
            if (organizationBranchCard)
                cards.push(organizationBranchCard);
        }
        if (id === 'organization-admin-client-portfolio') {
            if (organizationOpeningCutoverCard)
                cards.push(organizationOpeningCutoverCard);
            if (organizationSettlementInstructionsCard)
                cards.push(organizationSettlementInstructionsCard);
        }
        if (id === 'organization-admin-reference-data') {
            if (organizationMarketDataCard)
                cards.unshift(organizationMarketDataCard);
            if (organizationInstrumentCard)
                cards.unshift(organizationInstrumentCard);
        }
        return cards.length > 0 ? [groupFrom(template, id, cards)] : [];
    });
}
export const PORTFOLIO_MANAGEMENT_ORGANIZATION_ADMIN_HUB = Object.freeze({
    ...ORGANIZATION_ADMIN_HUB,
    groups: projectOrganizationGroups(ORGANIZATION_ADMIN_HUB.groups),
});
const PROMOTED_ORGANIZATION_CARDS = new Set([
    'card.organization.roles',
    'card.organization.accessInspector',
]);
export const PORTFOLIO_MANAGEMENT_ADMIN_HUB = Object.freeze({
    ...ADMIN_HUB,
    groups: [
        ...ADMIN_HUB.groups
            .filter((group) => !String(group.id).startsWith('organization-admin'))
            .map((group) => ({
            ...group,
            cards: activeCards(group).filter((card) => !card.id.startsWith('card.platform.')),
        }))
            .filter((group) => group.cards.length > 0),
        ...PORTFOLIO_MANAGEMENT_ORGANIZATION_ADMIN_HUB.groups.map((group) => ({
            ...group,
            cards: group.cards.map((card) => PROMOTED_ORGANIZATION_CARDS.has(card.id) ? { ...card, wip: false } : card),
        })),
    ],
});
